import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MapPin, Trash2, Building2 } from "lucide-react";
import api, { useTitle, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "profil", label: "Profilim" },
  { key: "favoriler", label: "Favorilerim" },
  { key: "talepler", label: "İletişim Taleplerim" },
  { key: "bildirimler", label: "Bildirimler" },
];

export default function CustomerPanel() {
  const { user, reload } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profil";
  const [favorites, setFavorites] = useState({ companies: [], resources: [] });
  const [requests, setRequests] = useState([]);
  const [notes, setNotes] = useState({ notifications: [], unread: 0 });
  const [profile, setProfile] = useState({ name: "", surname: "", phone: "" });
  useTitle("Müşteri Paneli");

  useEffect(() => {
    if (user === null) navigate("/giris");
    if (user) setProfile({ name: user.name, surname: user.surname, phone: user.phone || "" });
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    if (tab === "favoriler") api.get("/favorites").then((r) => setFavorites(r.data)).catch(() => {});
    if (tab === "talepler") api.get("/customer/requests").then((r) => setRequests(r.data)).catch(() => {});
    if (tab === "bildirimler") {
      api.get("/notifications").then((r) => {
        setNotes(r.data);
        api.post("/notifications/read").catch(() => {});
      }).catch(() => {});
    }
  }, [user, tab]);

  if (!user) return null;

  const unfav = async (f) => {
    await api.post("/favorites/toggle", f).catch((e) => toast.error(errMsg(e)));
    api.get("/favorites").then((r) => setFavorites(r.data));
  };

  const saveProfile = async (e) => {
    e.preventDefault();
    try {
      await api.put("/customer/profile", profile);
      toast.success("Profil güncellendi");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const statusText = { new: "Yeni", contacted: "Görüşüldü", closed: "Kapandı" };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="customer-panel">
      <h1 className="text-2xl font-extrabold text-white mb-1">Merhaba, {user.name}</h1>
      <p className="text-sm text-slate-400 mb-6">{user.email}</p>

      <div className="flex gap-2 overflow-x-auto pb-2 mb-6">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`customer-tab-${t.key}`} onClick={() => setParams({ tab: t.key })}
            className={`tab-btn ${tab === t.key ? "tab-btn-active" : ""}`}>{t.label}</button>
        ))}
      </div>

      {tab === "profil" && (
        <form onSubmit={saveProfile} className="card p-6 max-w-lg space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Ad</label><input data-testid="profile-name" className="input" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} /></div>
            <div><label className="label">Soyad</label><input data-testid="profile-surname" className="input" value={profile.surname} onChange={(e) => setProfile({ ...profile, surname: e.target.value })} /></div>
          </div>
          <div><label className="label">Telefon</label><input data-testid="profile-phone" className="input" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} /></div>
          <button data-testid="profile-save-btn" className="btn-gold">Kaydet</button>
        </form>
      )}

      {tab === "favoriler" && (
        <div className="space-y-6">
          <div>
            <h2 className="font-bold text-white mb-3">Favori Firmalar ({favorites.companies.length})</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {favorites.companies.map((c) => (
                <div key={c.id} className="card p-4 flex items-center gap-4" data-testid={`fav-company-${c.id}`}>
                  <img src={c.logo} alt="" className="w-14 h-14 rounded-xl object-cover" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/firma/${c.slug}`} className="font-bold text-sm text-white hover:text-[#D4AF37] truncate block">{c.name}</Link>
                    <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5"><MapPin size={11} /> {c.city} / {c.district}</div>
                  </div>
                  <button onClick={() => unfav({ company_id: c.id })} className="btn-danger !p-2" data-testid={`fav-remove-${c.id}`}><Trash2 size={14} /></button>
                </div>
              ))}
              {!favorites.companies.length && <p className="text-sm text-slate-500 col-span-2">Henüz favori firma yok.</p>}
            </div>
          </div>
          <div>
            <h2 className="font-bold text-white mb-3">Favori Kaynaklar ({favorites.resources.length})</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {favorites.resources.map((r) => (
                <div key={r.id} className="card p-4 flex items-center gap-4" data-testid={`fav-resource-${r.id}`}>
                  <Heart size={18} className="text-[#EF4444] fill-[#EF4444] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <Link to={`/firma/${r.company?.slug}/kaynak/${r.id}`} className="font-bold text-sm text-white hover:text-[#D4AF37] truncate block">{r.name}</Link>
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1"><Building2 size={11} /> {r.company?.name}</div>
                  </div>
                  <button onClick={() => unfav({ resource_id: r.id })} className="btn-danger !p-2"><Trash2 size={14} /></button>
                </div>
              ))}
              {!favorites.resources.length && <p className="text-sm text-slate-500 col-span-2">Henüz favori kaynak yok.</p>}
            </div>
          </div>
        </div>
      )}

      {tab === "talepler" && (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="bg-white/5"><tr><th className="th">Tip</th><th className="th">Firma</th><th className="th">Kaynak</th><th className="th">Tarih</th><th className="th">Mesaj</th><th className="th">Durum</th></tr></thead>
            <tbody className="divide-y divide-white/5">
              {requests.map((r) => (
                <tr key={r.id} data-testid={`request-row-${r.id}`}>
                  <td className="td">{r.type === "reservation" ? <span className="badge-gold">Rezervasyon</span> : <span className="badge-green">İletişim</span>}</td>
                  <td className="td font-semibold">{r.company_name}</td>
                  <td className="td">{r.resource_name || "-"}</td>
                  <td className="td">{r.event_date || "-"}</td>
                  <td className="td max-w-[220px] truncate">{r.message || "-"}</td>
                  <td className="td"><span className={r.status === "new" ? "badge-gold" : r.status === "contacted" ? "badge-green" : "badge-red"}>{statusText[r.status] || r.status}</span></td>
                </tr>
              ))}
              {!requests.length && <tr><td className="td text-slate-500" colSpan={6}>Henüz iletişim talebi oluşturmadınız.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "bildirimler" && (
        <div className="space-y-3">
          {notes.notifications.map((n) => (
            <div key={n.id} className={`card p-4 ${!n.read ? "border-[#D4AF37]/30" : ""}`} data-testid={`notification-${n.id}`}>
              <div className="font-semibold text-sm text-white">{n.title}</div>
              <div className="text-sm text-slate-400 mt-0.5">{n.body}</div>
              <div className="text-xs text-slate-600 mt-1">{new Date(n.created_at).toLocaleString("tr-TR")}</div>
            </div>
          ))}
          {!notes.notifications.length && <p className="text-sm text-slate-500">Bildiriminiz yok.</p>}
        </div>
      )}
    </div>
  );
}
